/* eslint-disable no-new-wrappers */
import React, { useState, useEffect, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import useApartmentsApi from '../../hooks/apartments/useApartmentsApi'
import useBuildingsApi from '../../hooks/buildings/useBuildingsApi'
import DeleteConfirmationModal from '../common/DeleteConfirmationModal'
import useUserEventsApi from '../../hooks/userEvents/useUserEventsApi'
import CustomTable from '../customTable/CustomTable'
import ShowImageTable from '../common/ShowImageTable'
import ListHeader from '../common/ListHeader'
import { useIsSecurityUserWithLoading } from '../../hooks/useIsSecurityUserWithLoading'
import InvitationsMembersModal from './InvitationsMembersModal'

export default function UserEventsList({ building }) {
  const [selectedInvitationToDelete, setSelectedInvitationToDelete] =
    useState(null)
  const [{ eventsState }, methods] = useUserEventsApi()
  const { isSecurityUser, loading } = useIsSecurityUserWithLoading()
  const isBuilding = building?.type === 'Building'
  const [invitationsMembers, setInvitationsMembers] = useState(false)

  const getDayMonthYear = (dateTimeString) => {
    const dateObject = new Date(dateTimeString)
    const day = String(dateObject.getUTCDate()).padStart(2, '0')
    const month = String(dateObject.getUTCMonth() + 1).padStart(2, '0') // Adding 1 to the month as it's zero-based.
    const year = String(dateObject.getUTCFullYear())
    // Return the formatted string
    return `${day}-${month}-${year}`
  }

  const columns = useMemo(
    () =>
      [
        { id: 'name', label: 'Evento', minWidth: 130 },
        {
          id: 'date',
          label: 'Fecha',
          minWidth: 250,
          format: (value) => getDayMonthYear(value.startTime),
          subRow: (subRow) => (
            <ShowImageTable
              imageUrl={subRow.selfie}
              name={`${subRow.firstName} ${subRow.lastName}`}
              ci={subRow.ci || subRow.email}
            />
          ),
        },
        {
          id: 'startTime',
          label: 'Hora inicio',
          minWidth: 120,
          format: (value) => getHoursAndMinutes(value.startTime),
        },
        {
          id: 'endTime',
          label: 'Hora fin',
          minWidth: 100,
          format: (value) => getHoursAndMinutes(value.endTime),
        },
        {
          id: 'user.name',
          label: 'Organizador',
          minWidth: 100,
          align: 'center',
          format: (value) => `${value.user.name} ${value.user.surname}`,
        },
        {
          id: 'user.apartment',
          label: isBuilding
            ? 'Edificio/Apto'
            : isSecurityUser
            ? 'Unidad'
            : 'Barrio/Unidad',
          minWidth: 150,
          align: 'center',
          format: (value) =>
            isSecurityUser
              ? value?.user?.apartments[0]?.name
              : `${value?.building?.name}/${value?.user?.apartments[0]?.name}`,
        },
        {
          id: 'invitationsLength',
          label: 'Cantidad invitados',
          minWidth: 100,
          align: 'center',
          format: (value) => value.invitations.length,
        },
        !isSecurityUser && {
          id: 'userLimit',
          label: 'Límite invitados',
          minWidth: 100,
          align: 'center',
        },
        { id: '', label: '', minWidth: 5, align: 'center' },
      ].filter(Boolean),
    [isSecurityUser, isBuilding]
  )

  const options = (userEvent) => [
    {
      key: 'delete',
      label: 'Eliminar',
      onClick: () => {
        setSelectedInvitationToDelete(userEvent)
      },
    },
  ]

  const userEvents = eventsState?.events
  const [selectedBuilding, setSelectedBuilding] = useState(building || false)
  const [selectedApartment, setSelectedApartment] = useState(null)
  const [, { getBuildings }] = useBuildingsApi()
  const [, { getApartmentsFromBuilding, getApartments }] = useApartmentsApi()

  useEffect(() => {
    const fetchData = async () => {
      getBuildings()
      getApartments()
      if (selectedBuilding) {
        setSelectedApartment(null)
        const fetchApartmentsFromBuilding = async () => {
          getApartmentsFromBuilding(selectedBuilding.id)
        }
        fetchApartmentsFromBuilding()
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (selectedBuilding) {
      setSelectedApartment(null)
      const fetchApartmentsFromBuilding = async () => {
        getApartmentsFromBuilding(selectedBuilding.id)
      }
      fetchApartmentsFromBuilding()
    }
  }, [selectedBuilding?.id])

  const getHoursAndMinutes = (dateTimeString) => {
    // Convert the input string to a Date object
    const dateObject = new Date(dateTimeString)

    // Extract hours and minutes from the Date object
    const hours = String(dateObject.getUTCHours()).padStart(2, '0')
    const minutes = String(dateObject.getUTCMinutes()).padStart(2, '0')

    // Return the formatted string
    return hours + ':' + minutes
  }
  if (loading) return null

  return (
    <>
      <ListHeader bigTitle="Eventos" canCreate={false} />
      <CustomTable
        loading={eventsState.isLoading}
        columns={columns}
        data={userEvents || []}
        getData={(params) => {
          methods.getEvents(params)
        }}
        buildingFilter={!building}
        building={building}
        apartmentFilter={true}
        searchTitle={'Nombre, apellido o documento de identidad'}
        searchPlaceholder={
          'Ingrese un nombre, apellido o documento de identidad'
        }
        options={options}
        serverPagination={true}
        serverCount={eventsState?.count}
        handleRowClick={(row) => {
          setInvitationsMembers(row.invitations)
        }}
      ></CustomTable>
      <DeleteConfirmationModal
        open={!!selectedInvitationToDelete}
        invitation={selectedInvitationToDelete}
        onClose={() => setSelectedInvitationToDelete(null)}
        onDelete={(userEvent) => {
          methods.deleteUserEvent(userEvent.id)
          setSelectedInvitationToDelete(null)
        }}
      />
      <InvitationsMembersModal
        open={invitationsMembers}
        invitationsMembers={invitationsMembers}
        onClose={() => setInvitationsMembers(false)}
      ></InvitationsMembersModal>
    </>
  )
}
